import {
  metrics,
  trace,
  type Counter,
  type Histogram,
  type Meter,
  type UpDownCounter,
} from '@opentelemetry/api';
import {
  OTLPMetricExporter,
  type OTLPMetricExporterOptions,
} from '@opentelemetry/exporter-metrics-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ConsoleMetricExporter,
  MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions';

import { isBrowser } from '@shared-web/utils/runtime';

import type { ClientTelemetryConfig } from '../config';

const DEFAULT_METRIC_EXPORT_INTERVAL_MS = 60000; // 1 minute

let meterProvider: MeterProvider | null = null;
let metricsUnloadHandler: (() => void) | null = null;

export interface MetricsSetupOptions {
  exportIntervalMillis?: number;
}

export function setupMetrics(
  config: ClientTelemetryConfig,
  options: MetricsSetupOptions = {},
): void {
  if (meterProvider) {
    console.warn('[OpenTelemetry] Metrics already initialized');
    return;
  }

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_VERSION]: config.serviceVersion,
    'deployment.environment': config.environment,
  });

  const exporter = createMetricExporter(config);
  const reader = new PeriodicExportingMetricReader({
    exporter,
    exportIntervalMillis:
      options.exportIntervalMillis ?? DEFAULT_METRIC_EXPORT_INTERVAL_MS,
  });

  meterProvider = new MeterProvider({
    resource,
    readers: [reader],
  });

  metrics.setGlobalMeterProvider(meterProvider);

  if (isBrowser()) {
    metricsUnloadHandler = () => {
      meterProvider?.forceFlush();
    };
    window.addEventListener('pagehide', metricsUnloadHandler);
  }

  if (config.debug) {
    console.log('[OpenTelemetry] Metrics initialized');
  }
}

function createMetricExporter(config: ClientTelemetryConfig) {
  if (config.debug) {
    return new ConsoleMetricExporter();
  }

  const exporterConfig: OTLPMetricExporterOptions = {
    url: config.metricsEndpoint,
    headers: {},
  };

  return new OTLPMetricExporter(exporterConfig);
}

export function getMeter(name: string = 'default', version?: string): Meter {
  if (!meterProvider) {
    return metrics.getMeter(name, version);
  }

  return meterProvider.getMeter(name, version);
}

/**
 * Extracts trace context for metric exemplars.
 * Exemplars link metric data points to specific traces for drill-down analysis.
 *
 * @see https://opentelemetry.io/docs/specs/otel/metrics/data-model/#exemplars
 */
function getExemplarContext(): Record<string, string> {
  const span = trace.getActiveSpan();
  if (!span) return {};

  const ctx = span.spanContext();
  return {
    trace_id: ctx.traceId,
    span_id: ctx.spanId,
  };
}

/**
 * Pre-configured common metrics manager.
 * Uses lazy initialization to ensure MeterProvider is ready before creating meters.
 *
 * Features:
 * - Lazy meter initialization (waits for MeterProvider)
 * - Automatic exemplar injection (links metrics to traces)
 * - Cached instrument instances (prevents duplicate creation)
 */
class MetricsManager {
  private _meter: Meter | null = null;
  private meterName: string;
  private counters = new Map<string, Counter>();
  private histograms = new Map<string, Histogram>();
  private upDownCounters = new Map<string, UpDownCounter>();

  constructor(meterName: string = 'x-web-frontend') {
    this.meterName = meterName;
  }

  private get meter(): Meter {
    if (!this._meter) {
      this._meter = getMeter(this.meterName);
    }
    return this._meter;
  }

  // Counter methods
  createCounter(name: string, description?: string, unit?: string): Counter {
    if (this.counters.has(name)) {
      return this.counters.get(name)!;
    }

    const counter = this.meter.createCounter(name, {
      description,
      unit,
    });

    this.counters.set(name, counter);
    return counter;
  }

  incrementCounter(
    name: string,
    value: number = 1,
    attributes?: Record<string, string | number>,
  ) {
    const counter = this.counters.get(name) || this.createCounter(name);
    counter.add(value, attributes);
  }

  // Histogram methods
  createHistogram(
    name: string,
    description?: string,
    unit?: string,
  ): Histogram {
    if (this.histograms.has(name)) {
      return this.histograms.get(name)!;
    }

    const histogram = this.meter.createHistogram(name, {
      description,
      unit,
    });

    this.histograms.set(name, histogram);
    return histogram;
  }

  recordHistogram(
    name: string,
    value: number,
    attributes?: Record<string, string | number>,
  ) {
    const histogram = this.histograms.get(name) || this.createHistogram(name);
    histogram.record(value, attributes);
  }

  // UpDownCounter methods
  createUpDownCounter(
    name: string,
    description?: string,
    unit?: string,
  ): UpDownCounter {
    if (this.upDownCounters.has(name)) {
      return this.upDownCounters.get(name)!;
    }

    const upDownCounter = this.meter.createUpDownCounter(name, {
      description,
      unit,
    });

    this.upDownCounters.set(name, upDownCounter);
    return upDownCounter;
  }

  updateUpDownCounter(
    name: string,
    value: number,
    attributes?: Record<string, string | number>,
  ) {
    const upDownCounter =
      this.upDownCounters.get(name) || this.createUpDownCounter(name);
    upDownCounter.add(value, attributes);
  }

  // Common web metrics with exemplar support

  recordPageLoad(duration: number, page: string) {
    this.recordHistogram('page_load_duration', duration, {
      page,
      ...getExemplarContext(),
    });
  }

  recordApiCall(duration: number, endpoint: string, status: number) {
    const exemplar = getExemplarContext();
    this.recordHistogram('api_call_duration', duration, {
      endpoint,
      status: status.toString(),
      ...exemplar,
    });
    this.incrementCounter('api_calls_total', 1, {
      endpoint,
      status: status.toString(),
    });
  }

  recordUserAction(action: string, component?: string) {
    this.incrementCounter('user_actions_total', 1, {
      action,
      ...(component && { component }),
      ...getExemplarContext(),
    });
  }

  recordError(errorType: string, component?: string) {
    this.incrementCounter('errors_total', 1, {
      error_type: errorType,
      ...(component && { component }),
      ...getExemplarContext(),
    });
  }

  recordActiveUsers(count: number) {
    this.updateUpDownCounter('active_users', count);
  }

  recordMemoryUsage(bytes: number) {
    this.recordHistogram('memory_usage_bytes', bytes);
  }
}

// Export singleton instance
export const metricsManager = new MetricsManager();

// Convenience functions
export const recordPageLoad = (duration: number, page: string) =>
  metricsManager.recordPageLoad(duration, page);

export const recordApiCall = (
  duration: number,
  endpoint: string,
  status: number,
) => metricsManager.recordApiCall(duration, endpoint, status);

export const recordUserAction = (action: string, component?: string) =>
  metricsManager.recordUserAction(action, component);

export const recordError = (errorType: string, component?: string) =>
  metricsManager.recordError(errorType, component);

export const recordActiveUsers = (count: number) =>
  metricsManager.recordActiveUsers(count);

export const recordMemoryUsage = (bytes: number) =>
  metricsManager.recordMemoryUsage(bytes);

export function isMetricsReady(): boolean {
  return meterProvider !== null;
}

export async function shutdownMetrics(): Promise<void> {
  if (metricsUnloadHandler && isBrowser()) {
    window.removeEventListener('pagehide', metricsUnloadHandler);
    metricsUnloadHandler = null;
  }

  if (meterProvider) {
    await meterProvider.shutdown();
    meterProvider = null;
  }
}
